import {
    useState,
    useRef,
    ReactNode,
    cloneElement,
    ReactElement,
    isValidElement,
    useEffect,
} from 'react';
import { ensureButtons } from '../Button/ensureButton.fn';

import { cn } from '../../utils';
import {
    flip,
    offset,
    arrow,
    autoUpdate,
    Placement,
    useFloating,
    useDismiss,
    useInteractions,
} from '@floating-ui/react';
import Button from '../Button/Button';

type ConfirmPopoverProps = {
    action?: ReactNode;
    children?: ReactNode;
    content: ReactNode;
    className?: string | object;
    placement?: Placement;
    open?: boolean;
    onClose?: () => void;
};

export default function ConfirmPopover({
    className,
    placement = 'top',
    content = '',
    action,
    children,
    open = false,
    onClose = undefined,
}: ConfirmPopoverProps) {
    const arrowRef = useRef(null);
    const [popover, setPopover] = useState(false);
    const handleClose = () => {
        onClose && onClose();
        setPopover(false);
    };

    useEffect(() => {
        if (open) {
            setPopover(true);
        } else {
            handleClose();
        }
    }, [open]);

    const {
        refs,
        floatingStyles,
        middlewareData,
        placement: position,
        context,
    } = useFloating({
        whileElementsMounted: autoUpdate,
        placement: placement,
        strategy: 'fixed',
        middleware: [
            flip({
                crossAxis: true,
                fallbackStrategy: 'bestFit',
                fallbackAxisSideDirection: 'start',
            }),
            offset(12),
            arrow({
                element: arrowRef,
            }),
        ],
        open: popover,
        onOpenChange: handleClose,
    });

    const dismiss = useDismiss(context, {
        outsidePress: true,
        escapeKey: true,
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([dismiss]);

    const side = position.split('-')[0];

    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[side];

    const arrowLocation = middlewareData.arrow;

    const arrowRotate: { [key: string]: string } = {
        top: '0deg',
        right: '90deg',
        bottom: '180deg',
        left: '270deg',
    };

    const arrowSide: { [key: string]: string } = {
        top: '-13px',
        right: '-19px',
        left: '-19px',
        bottom: '-13px',
    };

    if (ensureButtons(children) && isValidElement(children)) {
        const childOnclick = children?.props?.onClick;
        children = cloneElement(children as ReactElement, {
            onClick: () => {
                setPopover(!popover);
                childOnclick && childOnclick();
            },
        });
    }

    return (
        <div data-testid="ConfirmPopover" className="alli-relative">
            {popover && (
                <div
                    data-testid="popover"
                    style={floatingStyles}
                    ref={refs.setFloating}
                    className={cn(
                        className,
                        ' alli-relative alli-z-20 alli-w-max alli-max-w-80 alli-rounded-lg alli-border alli-border-gray-400 alli-bg-white alli-p-4 alli-shadow-lg',
                        { 'alli-hidden': !popover, 'alli-block': popover },
                    )}
                    {...getFloatingProps()}>
                    {arrowLocation && (
                        <div
                            ref={arrowRef}
                            id="arrow"
                            className="arrow alli-absolute alli-z-0 alli-h-0 alli-w-0"
                            style={{
                                borderLeft: '13px solid transparent',
                                borderRight: '13px solid transparent',
                                borderTop: '13px solid #e8e8e8',
                                left:
                                    arrowLocation.x != null
                                        ? `${arrowLocation.x}px`
                                        : '',
                                top:
                                    arrowLocation.y != null
                                        ? `${arrowLocation.y}px`
                                        : '',
                                [staticSide as string]: arrowSide[side],
                                transform: `rotate(${arrowRotate[side]})`,
                            }}
                        />
                    )}
                    <div>
                        {content}
                        {action !== undefined && (
                            <div className="alli-mt-2 alli-flex alli-justify-end alli-gap-2">
                                <Button
                                    data-testid="closeAction"
                                    onClick={handleClose}
                                    variant="tertiary">
                                    Close
                                </Button>
                                {action}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
                className="alli-max-w-fit">
                {children}
            </div>
        </div>
    );
}
